import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import clsx from "clsx";

import styles from "./tabs.module.css";

function tabIdFragment(v: string): string {
  const s = v.replace(/[^a-zA-Z0-9_-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return s.length > 0 ? s : "tab";
}

type TabsContextValue = {
  value: string;
  onValueChange: (next: string) => void;
  idPrefix: string;
  variant: TabsVariant;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx == null) {
    throw new Error(`${component} must be used within <Tabs>`);
  }
  return ctx;
}

export interface TabsProps {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
  className?: string;
  variant?: TabsVariant;
  /** Base string for stable `id` / `aria-*` wiring; defaults to a React `useId()`-derived prefix. */
  idPrefix?: string;
}

export type TabsVariant = "default" | "card";

export function Tabs({
  value,
  onValueChange,
  children,
  className,
  idPrefix,
  variant = "default",
}: TabsProps) {
  const reactId = useId();
  const resolvedPrefix = idPrefix ?? `tabs-${reactId.replace(/:/g, "")}`;

  const ctx = useMemo<TabsContextValue>(
    () => ({
      value,
      onValueChange,
      idPrefix: resolvedPrefix,
      variant,
    }),
    [value, onValueChange, resolvedPrefix, variant],
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div className={clsx(styles.tabsRoot, className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  const { variant } = useTabsContext("TabsList");
  return (
    <div
      className={clsx(
        styles.list,
        variant === "card" && styles.listCard,
        className,
      )}
      role="tablist"
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsTrigger({ value: triggerValue, children, className }: TabsTriggerProps) {
  const { value, onValueChange, idPrefix, variant } =
    useTabsContext("TabsTrigger");
  const selected = value === triggerValue;
  const fragment = tabIdFragment(triggerValue);
  const triggerId = `${idPrefix}-tab-${fragment}`;
  const panelId = `${idPrefix}-panel-${fragment}`;

  const focusAdjacent = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, direction: -1 | 1) => {
      const list = e.currentTarget.closest('[role="tablist"]');
      if (list == null) return;
      const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
      const i = tabs.indexOf(e.currentTarget);
      if (i < 0) return;
      const next = tabs[(i + direction + tabs.length) % tabs.length];
      const nextVal = next?.dataset.tabValue;
      if (next == null || nextVal == null) return;
      next.focus();
      onValueChange(nextVal);
    },
    [onValueChange],
  );

  return (
    <button
      type="button"
      id={triggerId}
      role="tab"
      className={clsx(
        styles.trigger,
        variant === "card" && styles.triggerCard,
        className,
      )}
      tabIndex={selected ? 0 : -1}
      aria-selected={selected}
      aria-controls={panelId}
      data-state={selected ? "active" : "inactive"}
      data-tab-value={triggerValue}
      onClick={() => {
        onValueChange(triggerValue);
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          focusAdjacent(e, -1);
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          focusAdjacent(e, 1);
        }
      }}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value: contentValue, children, className }: TabsContentProps) {
  const { value, idPrefix } = useTabsContext("TabsContent");
  const active = value === contentValue;
  const fragment = tabIdFragment(contentValue);
  const triggerId = `${idPrefix}-tab-${fragment}`;
  const panelId = `${idPrefix}-panel-${fragment}`;

  return (
    <div
      id={panelId}
      role="tabpanel"
      className={clsx(styles.panel, className)}
      hidden={!active}
      aria-labelledby={triggerId}
      data-state={active ? "active" : "inactive"}
    >
      {children}
    </div>
  );
}
